import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { ReleasesService } from '../../core/services/releases.service';
import { ShellStateService } from '../../core/services/shell-state.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../core/analytics/events';
import { TranslocoPipe } from '@jsverse/transloco';
import type {
  ReleaseEntry,
  ReleaseLink,
  ReleaseMedia,
} from '../../core/constants/releases';

/**
 * im-whats-new-panel — right-anchored slide-in for release notes.
 *
 * Mirrors the project-detail-panel chrome (backdrop overlay, 540px,
 * 350ms cubic-bezier slide). Mounted once globally inside app-shell;
 * opened via shellState.openWhatsNew() (from the feedback widget,
 * the command palette, or any future surface).
 *
 * Behaviour:
 *   - Lists every visible release entry (newest first).
 *   - Calls releases.markAllSeen() on first open while there's still
 *     unread content. Idempotent — re-opens are no-ops.
 *   - ESC key + backdrop click close via shellState.closeWhatsNew().
 *
 * The component is intentionally light on per-entry interactivity —
 * each release entry is a card the user reads. Future surfaces (deep
 * links to a single entry, per-entry comments) would extend this
 * component rather than spawn a new one.
 */
@Component({
  selector: 'im-whats-new-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, PaperIconComponent, PaperButtonComponent,
    TranslocoPipe,
  ],
  templateUrl: './whats-new-panel.component.html',
  styleUrl: './whats-new-panel.component.scss',
})
export class WhatsNewPanelComponent {
  protected readonly shellState = inject(ShellStateService);
  protected readonly releases = inject(ReleasesService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly open = this.shellState.whatsNewOpen;

  /** All visible release entries — newest first. */
  protected readonly entries = this.releases.visibleReleases;

  /** Tracks the last open transition so we only auto-mark-as-seen
   *  on the false → true edge, not on every open() read. */
  private wasOpen = false;

  /** Per-open set of release ids that have already fired the
   *  WHATS_NEW_ENTRY_VIEWED event. Cleared on close so re-opening
   *  the panel emits fresh impressions. */
  private viewedThisSession = new Set<string>();

  /** Snapshot of which release ids were unread at the moment the
   *  panel opened. The reactive isUnread() binding flips to false
   *  almost immediately after open (auto-mark-on-view), so we
   *  freeze the state here for the analytics ENTRY_VIEWED payload. */
  private unreadSnapshot = new Set<string>();

  /** IntersectionObserver instance — created on open, torn down on
   *  close. Watches every .wn-entry that scrolls into the viewport. */
  private viewObserver: IntersectionObserver | null = null;

  constructor() {
    // Auto-mark on view: when the panel opens AND there are unread
    // entries, fast-forward the seen pointer. Also fires the
    // WHATS_NEW_PANEL_OPENED event with the source stamped by the
    // caller (or 'direct' if none) — single point of truth so future
    // call sites only need to call shellState.openWhatsNew(source).
    effect(() => {
      const isOpen = this.open();
      const wasOpen = this.wasOpen;
      this.wasOpen = isOpen;

      if (isOpen && !wasOpen) {
        // Snapshot the unread state BEFORE the auto-mark so the
        // analytics event reflects what the user is about to see.
        const unreadBefore = this.releases.unreadCount();
        const latest = this.releases.latestId();

        // Freeze which entries were unread for the duration of this
        // open — used by the IntersectionObserver to stamp the
        // ENTRY_VIEWED payload with was_unread accurately even
        // though the live signal flips to false right after.
        this.unreadSnapshot = new Set(
          this.entries().filter(e => this.releases.isUnread(e)).map(e => e.id),
        );

        this.analytics.capture(ANALYTICS_EVENTS.WHATS_NEW_PANEL_OPENED, {
          source: this.shellState.whatsNewSource(),
          unread_count: unreadBefore,
          latest_release_id: latest,
        });

        if (unreadBefore > 0) {
          // Defer one frame so the unread badges render in the user's
          // first paint of the panel before we clear them.
          queueMicrotask(() => this.releases.markAllSeen('panel-opened'));
        }

        // Wire up the IntersectionObserver after the next frame so
        // the entry rows have laid out. queueMicrotask + rAF gives
        // us a stable DOM to attach to.
        queueMicrotask(() => requestAnimationFrame(() => this.attachViewObserver()));
      }

      if (!isOpen && wasOpen) {
        this.detachViewObserver();
        this.viewedThisSession.clear();
        this.unreadSnapshot.clear();
      }
    });

    this.destroyRef.onDestroy(() => this.detachViewObserver());
  }

  /** Wire IntersectionObserver to every .wn-entry currently in the DOM.
   *  Each entry that crosses 50% visibility fires WHATS_NEW_ENTRY_VIEWED
   *  exactly once per panel-open session (deduped via viewedThisSession). */
  private attachViewObserver(): void {
    if (typeof IntersectionObserver === 'undefined') return;
    this.detachViewObserver();
    const rows = document.querySelectorAll<HTMLElement>('.wn-entry[data-release-id]');
    if (rows.length === 0) return;

    this.viewObserver = new IntersectionObserver(
      (intersections) => {
        for (const intersection of intersections) {
          if (!intersection.isIntersecting) continue;
          const el = intersection.target as HTMLElement;
          const id = el.dataset['releaseId'];
          if (!id || this.viewedThisSession.has(id)) continue;
          this.viewedThisSession.add(id);

          const entry = this.entries().find(e => e.id === id);
          if (!entry) continue;
          const position = this.entries().findIndex(e => e.id === id);
          this.analytics.capture(ANALYTICS_EVENTS.WHATS_NEW_ENTRY_VIEWED, {
            release_id: entry.id,
            release_date: entry.date,
            // Read from the frozen open-time snapshot — the live
            // isUnread() signal has already flipped to false by now.
            was_unread: this.unreadSnapshot.has(entry.id),
            position,
          });
          this.viewObserver?.unobserve(el);
        }
      },
      {
        threshold: 0.5,
        // The panel body scrolls, not the viewport — the observer
        // needs to use the body as its root or large entries below
        // the fold register as never-visible.
        root: document.querySelector('.wn-panel__body'),
      },
    );

    rows.forEach(row => this.viewObserver?.observe(row));
  }

  private detachViewObserver(): void {
    this.viewObserver?.disconnect();
    this.viewObserver = null;
  }

  /** External link click — captures outbound URL + label so we can
   *  see which links pull users out of the panel. */
  protected onLinkClick(entry: ReleaseEntry, link: ReleaseLink): void {
    this.analytics.capture(ANALYTICS_EVENTS.WHATS_NEW_LINK_CLICKED, {
      release_id: entry.id,
      href: link.url,
      label: link.label,
    });
  }

  // ── Keyboard ───────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open()) return;
    this.shellState.closeWhatsNew();
  }

  // ── Helpers ────────────────────────────────────────────

  protected isUnread(entry: ReleaseEntry): boolean {
    return this.releases.isUnread(entry);
  }

  /** "9 May 2026" — short and locale-aware. */
  protected formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /** YouTube privacy-mode embed URL. Returned as SafeResourceUrl so
   *  Angular's iframe sanitiser is satisfied. We never accept arbitrary
   *  URLs here — the input is a 11-char video id from the constant
   *  release feed (in-code, not user-submitted), so we know it's safe. */
  protected youtubeEmbed(videoId: string): SafeResourceUrl {
    const url = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /** Track-by for media renders — kind+src is unique within an entry. */
  protected trackMedia(_index: number, m: ReleaseMedia): string {
    return `${m.kind}:${m.src}`;
  }
}
